import Section from '@components/atoms/Section';
import { Heading2 } from '@components/atoms/Typography';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import GridLinkList from '@components/molecules/list/GridLinkList';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import { ProsAndCons } from '@components/molecules/slices/SliceProsCons';
import { SliceButtonLink } from '@components/molecules/SliceZone';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import FAQBlock from '@components/organisms/seo/FAQBlock';
import { CarrierId } from '@graphql/generated';
import COLORS from '@lib/constants/colors';
import ROUTES from '@lib/constants/routes';
import { aggregateRating } from '@lib/constants/seo';
import carriersContent, {
  CarrierInsuranceProduct,
} from '@lib/data/contents/carriersContent';
import airtableBases from '@lib/helpers/airtableBases';
import {
  getAssistanceInfo,
  getCoverageInfo,
  getInsurancePageInfo,
  InsurancePageId,
} from '@lib/helpers/texts';
import prisma from '@lib/services/prisma';
import { joinWords } from 'helpers';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ProductJsonLd } from 'next-seo';
import Link from 'next/link';
import React from 'react';

interface Props {
  insurancePage: InsurancePageId;
  uid: CarrierId;
  brands: {
    brand: string;
    brandSlug: string;
  }[];
  compareCarriers: {
    slug: string;
    name: string | undefined;
  }[];
}

const Page = ({ insurancePage, uid, brands, compareCarriers }: Props) => {
  const info = getInsurancePageInfo(insurancePage);

  if (!info) return null;

  const content = carriersContent[uid];

  const product = content.products.find(
    (p) => p.pageId === insurancePage,
  ) as CarrierInsuranceProduct;

  const coverages = [...new Set(product.plans.map((c) => c.coverages).flat())];

  return (
    <PageContainer
      title={`${content?.shortName} ${info?.productName} | Preços, Planos e Avaliações`}
      metaDescription={`${info?.productName} da ${content?.name} é bom? Confira sobre quanto custa, cotação ,o que cobre e muito mais no seguro de ${info.name} da ${content.shortName}`}
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      customAmpPage
    >
      <ProductJsonLd
        productName={`${info.productName} na ${content.name}`}
        description={`Cote, renove ou contrate o seguro de ${info.name} na ${content.name} em menos de 3 minutos.`}
        aggregateRating={aggregateRating}
      />
      <DefaultHeader
        pastel
        title={`Como funciona o ${info?.productName} da ${content?.shortName}`}
        subtitle={`Quer proteger seu ${info.name} com o seguro da ${content.shortName}? Aqui está tudo que você precisa saber sobre a ${content.shortName} no ${info.productName}`}
        buttonText="Simule agora"
      />

      <RichTextSection>
        <p>
          Pra quem deseja proteger seu veículo, a {content.name} é uma excelente
          escolha, por ser uma seguradora devidamente registrada na SUSEP, a{' '}
          {content.name} oferece várias opções e preços para quem deseja{' '}
          <Link href={ROUTES.insurancePage.home(insurancePage)}>
            <a>contratar um seguro de {info.name.toLowerCase()}</a>
          </Link>
          .
        </p>
        <ProsAndCons
          pros={
            <ul>
              {content.pros.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          }
          cons={
            content.cons.length ? (
              <ul>
                {content.cons.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            ) : null
          }
        />
        <div>
          <h2>
            Como funciona o seguro de {info.name} da {content.name}?
          </h2>
          <p>
            A {content.shortName} oferece {product.plans.length} planos de
            seguro para {info.name}, assim você consegue proteger seu veículo de
            várias formas e com {coverages.length} tipos diferentes de
            coberturas.
          </p>
          <h3>
            O que cobre o {info.productName} da {content.shortName}?
          </h3>
          <p>
            A {content.shortName} oferece as seguintes coberturas para o seguro
            de {info.name}, assim você consegue escolher a que mais combina com
            você
          </p>
          <ul>
            {coverages.map((c) => {
              const texts = getCoverageInfo({ type: c, value: 1 });
              return (
                <li key={c}>
                  <strong>{texts.name}</strong>
                  <br />
                  {texts.description}
                </li>
              );
            })}
          </ul>

          <h3>
            Planos de coberturas {content.shortName} {info.productName}
          </h3>
          <ul>
            {product.plans.map((plan) => (
              <li key={plan.name}>
                <strong>{plan.name}</strong>
                <br />
                Cobre:{' '}
                {joinWords(
                  plan.coverages.map(
                    (c) => getCoverageInfo({ type: c, value: 1 }).name,
                  ),
                )}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2>
            Quanto custa o seguro de {info.name} da {content.name}?
          </h2>
          <p>
            Isso é dificil dizes, pois o seguro varia muito, conforme o valor do
            veículo, índice de roubo e furto, idade do condutor principal, tipo
            de utilização e endereço onde o veículo passa a noite.
          </p>
          <p>
            Mas a melhor maneira de saber quanto vai custar o seguro da{' '}
            {content.name} é usar um comparador de {info.productName} online
            igual a Luna, basta responder algumas perguntas e você vai saber os
            valores das melhores seguradoras para {info.name}
          </p>
          <SliceButtonLink
            slice={{
              primary: {
                href: ROUTES.startForm(),
                text: 'Ver Preços',
              },
            }}
          />
        </div>
        <div>
          <h2>
            Como contratar o seguro de {info.name} da {content.shortName}{' '}
            online?
          </h2>
          <p>
            Como um dono de {info.name}, você pode facilmente contratar um{' '}
            {info.productName} da {content.name} online. Basta seguir os passos
            abaixo para contratar o{' '}
            <Link href={ROUTES.insurancePage.home(insurancePage)}>
              <a>melhor seguro de {info.name}</a>
            </Link>{' '}
            da {content.name} online:
          </p>
          <ul>
            <li>
              <Link href={ROUTES.startForm()}>
                <a>Inicie a contratação do seguro de {info.name} online</a>
              </Link>
            </li>
            <li>Informe os detalhes de como você usa o veículo</li>
            <li>Responda sobre quem é o condutor</li>
            <li>Informe os dados de contato</li>
            <li>
              Vários planos da {content.name} e outras seguradoras de{' '}
              {info.name} serão mostrados
            </li>
            <li>
              Escolha o melhor seguro de {info.name} conforme a sua necessidade
            </li>
            <li>
              Realize o pagamento do seguro de {info.name} via cartão de crédito
            </li>
            <li>
              A sua seguradora de {info.name} vai emitir a sua apólice online 🎉
            </li>
          </ul>
          <SliceButtonLink
            slice={{
              primary: {
                href: ROUTES.startForm(),
                text: `Cotar Seguro ${content.shortName}`,
              },
            }}
          />
        </div>
        <div>
          <h2>
            Quais assistências do seguro de {info.name} que a {content.name}?
          </h2>
          <p>
            {' '}
            A {content.shortName} oferece {product.assistances.length} de
            assistências 24 horas no seguro de {info.name}, confira abaixo:
          </p>
          {product.assistances.map((c) => {
            const texts = getAssistanceInfo({ type: c, limit: 1, value: 0 });
            return (
              <li key={c}>
                <strong>{texts.name}</strong>
                <br />
                {texts.description}
              </li>
            );
          })}
        </div>
        <div>
          <h2>A {content.name} é confiável?</h2>
          <p>
            O site Reclame Aqui é uma ótima plataforma para conhecermos melhor a
            reputação de uma seguradora no Brasil. Isso porque nesse espaço os
            clientes registram reclamações e avaliações sobre o atendimento e
            venda de produtos e serviços.
          </p>
          <p>
            Nos últimos 6 meses, a nota da{' '}
            <a
              href={`https://www.reclameaqui.com.br/empresa/${content.reclameAqui.shortName}`}
              target="_blank"
              rel="noreferrer"
            >
              {content.shortName} no Reclame Aqui
            </a>{' '}
            foi {content.reclameAqui.panel.avg} de 10, sendo considerada “
            {content.reclameAqui.performanceData.status}” pela plataforma,
            conforme pode ser visto abaixo.
          </p>
          <ul>
            <li>
              Nota da {content.shortName} no Reclame Aqui:{' '}
              {content.reclameAqui.panel.avg} de 10
            </li>
            <li>
              {content.reclameAqui.panel.index.answeredPercentual}% das
              reclamações respondidas
            </li>
            <li>
              Soluciona {content.reclameAqui.panel.index.solvedPercentual}% das
              reclamações
            </li>
            <li>
              A nota dos clientes para a {content.name} é{' '}
              {content.reclameAqui.panel.index.consumerScore} de 10
            </li>
          </ul>
          <p>
            Vale reforçar que diariamente chegam novas opiniões dos clientes,
            por isso é importante que você acompanhe o histórico da empresa para
            ver se os atendimentos têm obtido uma pontuação melhor ou pior.
          </p>
        </div>

        <div id="contatos">
          <h2>Telefones da {content.name}</h2>
          <p>
            Se for necessário falar com a {content.name} de maneira urgente por
            algum acidente com seu {info.name}, você poderá utilizar o telefone
            como principal referência de contato.
          </p>
          <ul>
            {content.contacts.map((c) => (
              <li key={c.name}>
                {c.name}: <a href={`tel:${c.phone}`}>{c.phone}</a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3>
            Como acionar o seguro de {info.name} da {content.name}?
          </h3>
          <p>
            Para acionar o seguro de {info.name} da {content.name} basta{' '}
            <a href="#contatos">clicar aqui</a> e ligar para um dos números,
            assim vão te oferecer todo suporte para seu veículo
          </p>
        </div>

        <FAQBlock
          items={[
            {
              question: `Como renovar o seguro de ${info.name} da ${content.shortName}?`,
              answer: (
                <p>
                  Para renovar o o seguro de {info.name} da {content.shortName}{' '}
                  basta{' '}
                  <Link href={ROUTES.startForm()} passHref>
                    <a>clicar aqui</a>
                  </Link>{' '}
                  e fazer uma cotação de renovação
                </p>
              ),
            },

            {
              question: `Quantos anos tem a ${content.name}?`,
              answer: (
                <p>
                  A {content.name} tem{' '}
                  {new Date().getFullYear() - content.since} anos de atuação no
                  mercado de seguros. Ela começou sua operação em seguros no ano
                  de {content.since}.
                </p>
              ),
            },
            {
              question: `Qual o CNPJ da ${content.name}?`,
              answer: (
                <p>
                  O CNPJ da {content.name} é {content.reclameAqui.cnpj}.
                </p>
              ),
            },
          ]}
        />

        <Heading2>Compare a {content.name} com outras seguradoras:</Heading2>
        <ul>
          {compareCarriers
            .filter((c) => c.slug !== content.id)
            .map((c) => (
              <li key={c.slug}>
                <Link
                  passHref
                  href={ROUTES.carriers.compare([
                    { slug: content.id },
                    { slug: c.slug },
                  ])}
                >
                  <a>{`${content.name} vs ${c.name}`}</a>
                </Link>
              </li>
            ))}
        </ul>
      </RichTextSection>
      <Section style={{ paddingTop: 0 }}>
        <BreadCrumbs
          links={[
            {
              label: 'Luna',
              href: ROUTES.root,
            },
            {
              label: info?.name || '',
              href: ROUTES.insurancePage.home(insurancePage),
            },
            {
              label: 'Seguradoras',
              href: ROUTES.insurancePage.carriers(insurancePage),
            },
            {
              label: content.shortName,
              href: ROUTES.insurancePage.carrier(insurancePage, content.id),
            },
          ]}
        />
      </Section>
      <Section pastel>
        <Heading2 center>
          Marcas de {info.name} que a {content.shortName} cobre
        </Heading2>
        <GridLinkList
          deskTemplate="1fr 1fr 1fr 1fr"
          links={brands.map((brand) => ({
            path: ROUTES.insurancePage.brand(insurancePage, brand.brandSlug),
            label: `Seguro ${brand.brand} ${content.shortName}`,
          }))}
        />
      </Section>
    </PageContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = Object.values(carriersContent)
    .map((content) =>
      content.products.map((p) =>
        ROUTES.insurancePage.carrier(p.pageId, content.id),
      ),
    )
    .flat();

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { insurancePage, uid } = params as {
    insurancePage: InsurancePageId;
    uid: CarrierId;
  };

  const info = getInsurancePageInfo(insurancePage);

  const brands = await prisma.fipeModel.findMany({
    where: {
      vehicleType: info?.type,
    },
    distinct: ['brand'],
    orderBy: { brand: 'asc' },
    select: {
      brand: true,
      brandSlug: true,
    },
  });

  const compareCarriers = await airtableBases.getCarriers();

  return {
    props: {
      insurancePage,
      uid,
      brands,
      compareCarriers: compareCarriers.map((c) => ({
        slug: c.slug,
        name: c.nome,
      })),
    },
  };
};

export default Page;
